import { memo, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { Flex, Modal, Tooltip } from 'antd'
import Button from '../../../../common/Button'
import { deleteRequest, getRequest, postRequest } from '../../../../../services/base'
import { NavContext } from '../../../../../context/global_context'
import { notifyError } from '../../../../../utils/notifications'
import SelectSalesCodesBody from './SelectSalesCodesBody'
import DiagramPreviewBody from './DiagramPreviewBody'

const FunctionalDiagramPreview = ({ vehicle, functionalDiagramId, hasMappedConnectors = false }) => {
  const [, navDispatch] = useContext(NavContext)
  const intl = useIntl()

  const [open, setOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewListName, setPreviewListName] = useState('')
  const [salesCodeList, setSalesCodeList] = useState([])
  const [salesCodesData, setSalesCodesData] = useState([])
  const [filterData, setFilterData] = useState([])
  const [selectedSalesCodes, setSelectedSalesCodes] = useState([])
  const [salesCodeListName, setSalesCodeListName] = useState('')
  const [importJson, setImportJson] = useState({ show: false, data: '' })
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [expandedDetails, setExpandedDetails] = useState({})
  const [modal, contextHolder] = Modal.useModal()

  const getSalesCodeList = async () => {
    let res = await getRequest(`/api/option_code_list/all/?vehicle=${vehicle}`)
    setSalesCodeList(res.data || [])
  }

  const getSalesCodes = async () => {
    let res = await getRequest(`/api/option_codes/options/?vehicle=${vehicle}`)
    // base.js returns {status: false} instead of rejecting. Falling through put
    // `undefined` in state, which crashed on the next render at salesCodesData.filter.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.salesCodesLoadError' }))
      return
    }
    setSalesCodesData(res?.data || [])
    setFilterData(res?.data || [])
  }

  const showDiagramPreview = () => {
    setOpen(true)
    setPreviewUrl(null)
    setPreviewListName('')
    setSelectedSalesCodes([])
    setImportJson({ show: false, data: '' })
    setSalesCodeListName('')
    setExpandedRowKeys([])
    getSalesCodeList()
    getSalesCodes()
  }

  const selectSalesCode = (id) => {
    setSelectedSalesCodes((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    )
  }

  const searchSalesCodes = (value) => {
    if (!value) {
      setFilterData(salesCodesData)
      return
    }
    setFilterData(
      salesCodesData.filter((code) =>
        code?.label?.toLowerCase().includes(value.toLowerCase())
      )
    )
  }

  const initializeBlockDiagramPreview = async (
    salesCodeListId,
    salesCodeData = [],
    listName = ''
  ) => {
    if (!salesCodeListId && salesCodeData.length === 0) {
      navDispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'functionalDiagram.selectAtLeastOneList' }) },
      })
      return
    }

    navDispatch({
      type: 'loader',
      data: { caption: intl.formatMessage({ id: 'functionalDiagram.loadingPreview' }), showLoader: true },
      notification: {},
    })

    const res = await postRequest(
      `/api/functional_diagram/preview/${vehicle}/${functionalDiagramId}/`,
      { salesCodeListId: salesCodeListId, sales_code_data: salesCodeData }
    )

    if (res?.status) {
      setPreviewListName(listName)
      setPreviewUrl(res.data?.preview_url)
    } else {
      // Clear the loader FIRST: the 'loader' reducer case overwrites
      // `notification`, so notifying before it wipes the toast in the same batch.
      navDispatch({ type: 'loader', data: {}, notification: {} })
      navDispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'functionalDiagram.previewError' }) },
      })
    }
  }

  const clearPreviewLoader = () => {
    navDispatch({ type: 'loader', data: {}, notification: {} })
  }

  const saveSalesCodeList = (codes) => {
    if (!salesCodeListName) {
      navDispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'functionalDiagram.enterListName' }) },
      })
      return
    }

    modal.confirm({
      title: intl.formatMessage({ id: 'functionalDiagram.saveListConfirmTitle' }),
      content: intl.formatMessage({ id: 'functionalDiagram.saveListConfirmContent' }),
      okText: intl.formatMessage({ id: 'forms.save' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      centered: true,
      maskClosable: true,
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'functionalDiagram.savingList' }), showLoader: true },
          notification: {},
        })
        let res = await postRequest(
          `/api/option_code_list/save_sales_code_list/?vehicle=${vehicle}`,
          { name: salesCodeListName, sales_code: codes, vehicle: vehicle }
        )
        if (res?.status) {
          navDispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'functionalDiagram.listSaveSuccess' }) },
          })
          const name = salesCodeListName
          setSalesCodeListName('')
          setSelectedSalesCodes([])
          setImportJson({ show: false, data: '' })
          getSalesCodeList()
          initializeBlockDiagramPreview(res.id, [], name)
        } else {
          navDispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'functionalDiagram.listSaveError' }) },
          })
        }
      },
    })
  }

  const parseAndSaveJsonSalesCodes = () => {
    try {
      let parsed = JSON.parse(importJson.data)
      if (parsed?.sales_codes?.length > 0) {
        saveSalesCodeList(parsed.sales_codes)
      } else {
        throw new Error('Invalid Json Given')
      }
    } catch (error) {
      navDispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'functionalDiagram.invalidJson' }) },
      })
    }
  }

  const toggleExpand = async (listId) => {
    if (expandedRowKeys.includes(listId)) {
      setExpandedRowKeys([])
      return
    }
    setExpandedRowKeys([listId])
    if (!expandedDetails[listId]) {
      let res = await getRequest(`/api/option_code_list/sales_code/${listId}/`)
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.salesCodeDetailsError' }))
        return
      }
      setExpandedDetails((prev) => ({ ...prev, [listId]: res?.data || [] }))
    }
  }

  const previewList = (list) => {
    initializeBlockDiagramPreview(list.id, [], list.name)
  }

  const deleteSalesCodeList = (list) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'functionalDiagram.deleteConfirmTitle' }),
      content: intl.formatMessage({ id: 'functionalDiagram.deleteListConfirmContent' }, { name: list.name }),
      okText: intl.formatMessage({ id: 'forms.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      centered: true,
      maskClosable: true,
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'functionalDiagram.deletingList' }), showLoader: true },
          notification: {},
        })
        const res = await deleteRequest(`/api/option_code_list/${list.id}/?vehicle=${vehicle}`)
        // After the loader dispatch above: a 'loader' action overwrites
        // `notification`. The result used to be discarded, so the success toast
        // below fired even when the delete failed.
        if (res?.status === false) {
          notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.listDeleteError' }))
          return
        }
        navDispatch({
          type: 'notification',
          data: { notification_type: 'success', msg: intl.formatMessage({ id: 'functionalDiagram.listDeletedSuccess' }) },
        })
        getSalesCodeList()
      },
    })
  }

  const backToSalesCodes = () => {
    setPreviewUrl(null)
    setPreviewListName('')
  }

  const closeModal = () => {
    setOpen(false)
    setPreviewUrl(null)
    setPreviewListName('')
    setSelectedSalesCodes([])
    setExpandedRowKeys([])
  }

  return (
    <>
      <Tooltip title={!hasMappedConnectors ? intl.formatMessage({ id: 'functionalDiagram.previewTooltipDisabled' }) : null}>
        <Button onClick={showDiagramPreview} disabled={!hasMappedConnectors} testId="fd-show-preview">
          {intl.formatMessage({ id: 'functionalDiagram.showPreview' })}
        </Button>
      </Tooltip>

      {open && (
        <Modal
          open={open}
          onCancel={closeModal}
          width={previewUrl ? "92%" : "90%"}
          footer={null}
          {...previewUrl ? {} : {centered: true}}
          className={`sales-code-preview-form-modal ${previewUrl ? 'preview-functional-diagram' : ''}`}
          title={
            previewUrl ? (
              <Flex gap={12} align="center">
                <Button type="link" className="link-button" onClick={backToSalesCodes} testId="fd-preview-back">
                  {intl.formatMessage({ id: 'functionalDiagram.backToSalesCodes' })}
                </Button>
                <span className="bolder">{previewListName}</span>
              </Flex>
            ) : (
              intl.formatMessage({ id: 'functionalDiagram.selectSalesCodesTitle' })
            )
          }
        >
          {previewUrl ? (
            <DiagramPreviewBody previewUrl={previewUrl} onLoad={clearPreviewLoader} />
          ) : (
            <SelectSalesCodesBody
              filterData={filterData}
              selectedSalesCodes={selectedSalesCodes}
              onToggleCode={selectSalesCode}
              onSearch={searchSalesCodes}
              importJson={importJson}
              setImportJson={setImportJson}
              salesCodeListName={salesCodeListName}
              setSalesCodeListName={setSalesCodeListName}
              onSaveGrid={() => saveSalesCodeList(selectedSalesCodes)}
              onSaveJson={parseAndSaveJsonSalesCodes}
              salesCodeList={salesCodeList}
              expandedRowKeys={expandedRowKeys}
              expandedDetails={expandedDetails}
              onToggleExpand={toggleExpand}
              onPreview={previewList}
              onDelete={deleteSalesCodeList}
            />
          )}
        </Modal>
      )}

      {contextHolder}
    </>
  )
}

export default memo(FunctionalDiagramPreview)
